/**
 * URL Search Parameters Hook
 *
 * Provides utilities for reading and updating URL query parameters
 * for search, filtering, and navigation.
 *
 * Supports the following URL parameters:
 * - s: Full-text search query
 * - tag: Tag ID filter
 * - project: Project ID filter
 * - customer: Customer ID filter
 * - from/to: Date range
 * - date: Specific date
 * - status: Status filter
 * - channel: Channel filter
 * - category: Category filter
 * - priority: Priority filter
 * - assignee: Assignee filter
 */

import { useMemo, useCallback } from 'react';
import type { FilterParams, ParsedFilters } from '../data/types';

// Declare globals for environments without DOM types
declare const window: {
  location: {
    search: string;
    pathname: string;
  };
  history: {
    replaceState: (data: unknown, unused: string, url: string) => void;
  };
} | undefined;

declare class URLSearchParams {
  constructor(init?: string);
  has(name: string): boolean;
  get(name: string): string | null;
  set(name: string, value: string): void;
  toString(): string;
}

// =============================================================================
// TYPES
// =============================================================================

export interface UseSearchParamsOptions {
  /** Custom URL getter (for SSR or testing) */
  getUrl?: () => string;
  /** Custom URL setter (for SSR or testing) */
  setUrl?: (url: string) => void;
}

export interface UseSearchParamsResult {
  /** Raw URL parameters as object */
  params: FilterParams;
  /** Parsed and typed filter values */
  filters: ParsedFilters;
  /** Current search query */
  search: string;
  /** Check if any filters are active */
  hasActiveFilters: boolean;
  /** Set a single parameter */
  setParam: (key: keyof FilterParams, value: string | null) => void;
  /** Set multiple parameters at once */
  setParams: (params: Partial<FilterParams>) => void;
  /** Clear a specific parameter */
  clearParam: (key: keyof FilterParams) => void;
  /** Clear all parameters */
  clearAllParams: () => void;
  /** Toggle a parameter value (for chips/tags) */
  toggleParam: (key: keyof FilterParams, value: string) => void;
  /** Get URL with updated params (for link building) */
  buildUrl: (params: Partial<FilterParams>) => string;
  /** Get clean URL string for current filters */
  toUrlString: () => string;
}

// =============================================================================
// IMPLEMENTATION
// =============================================================================

/**
 * Parse URL search params into FilterParams object
 */
function parseUrlParams(searchString: string): FilterParams {
  const urlParams = new URLSearchParams(searchString);
  const params: FilterParams = {};

  // Map URL params to FilterParams
  if (urlParams.has('s')) params.s = urlParams.get('s') || undefined;
  if (urlParams.has('tag')) params.tag = urlParams.get('tag') || undefined;
  if (urlParams.has('project')) params.project = urlParams.get('project') || undefined;
  if (urlParams.has('customer')) params.customer = urlParams.get('customer') || undefined;
  if (urlParams.has('from')) params.from = urlParams.get('from') || undefined;
  if (urlParams.has('to')) params.to = urlParams.get('to') || undefined;
  if (urlParams.has('date')) params.date = urlParams.get('date') || undefined;
  if (urlParams.has('status')) params.status = urlParams.get('status') || undefined;
  if (urlParams.has('channel')) params.channel = urlParams.get('channel') || undefined;
  if (urlParams.has('category')) params.category = urlParams.get('category') || undefined;
  if (urlParams.has('priority')) params.priority = urlParams.get('priority') || undefined;
  if (urlParams.has('assignee')) params.assignee = urlParams.get('assignee') || undefined;

  return params;
}

/**
 * Convert FilterParams to ParsedFilters with typed values
 */
function parseFilters(params: FilterParams): ParsedFilters {
  return {
    search: params.s || '',
    tagId: params.tag || null,
    projectId: params.project || null,
    customerId: params.customer || null,
    dateRange: {
      from: params.from ? new Date(params.from) : null,
      to: params.to ? new Date(params.to) : null,
    },
    date: params.date ? new Date(params.date) : null,
    status: params.status || null,
    channel: params.channel || null,
    category: params.category || null,
    priority: params.priority || null,
    assigneeId: params.assignee || null,
  };
}

/**
 * Build URL search string from FilterParams
 */
function buildSearchString(params: FilterParams): string {
  const urlParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      urlParams.set(key, String(value));
    }
  });

  const searchString = urlParams.toString();
  return searchString ? `?${searchString}` : '';
}

/**
 * Hook for managing URL search parameters
 */
export function useSearchParams(options: UseSearchParamsOptions = {}): UseSearchParamsResult {
  const {
    getUrl = () => (typeof window !== 'undefined' ? window.location.search : ''),
    setUrl = (url: string) => {
      if (typeof window !== 'undefined') {
        const newUrl = `${window.location.pathname}${url}`;
        window.history.replaceState({}, '', newUrl);
      }
    },
  } = options;

  // Parse current URL params
  const params = useMemo(() => parseUrlParams(getUrl()), [getUrl]);

  // Parse into typed filters
  const filters = useMemo(() => parseFilters(params), [params]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(params).some((v) => v !== undefined && v !== null && v !== '');
  }, [params]);

  // Set a single parameter
  const setParam = useCallback(
    (key: keyof FilterParams, value: string | null) => {
      const newParams = { ...params };
      if (value === null || value === '') {
        delete newParams[key];
      } else {
        newParams[key] = value;
      }
      setUrl(buildSearchString(newParams));
    },
    [params, setUrl]
  );

  // Set multiple parameters
  const setParams = useCallback(
    (newParams: Partial<FilterParams>) => {
      const merged = { ...params };
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          delete merged[key as keyof FilterParams];
        } else {
          merged[key as keyof FilterParams] = value;
        }
      });
      setUrl(buildSearchString(merged));
    },
    [params, setUrl]
  );

  // Clear a specific parameter
  const clearParam = useCallback(
    (key: keyof FilterParams) => {
      setParam(key, null);
    },
    [setParam]
  );

  // Clear all parameters
  const clearAllParams = useCallback(() => {
    setUrl('');
  }, [setUrl]);

  // Toggle a parameter value
  const toggleParam = useCallback(
    (key: keyof FilterParams, value: string) => {
      if (params[key] === value) {
        clearParam(key);
      } else {
        setParam(key, value);
      }
    },
    [params, setParam, clearParam]
  );

  // Build URL with updated params
  const buildUrl = useCallback(
    (newParams: Partial<FilterParams>) => {
      const merged = { ...params, ...newParams };
      Object.keys(merged).forEach((key) => {
        if (merged[key as keyof FilterParams] === null || merged[key as keyof FilterParams] === '') {
          delete merged[key as keyof FilterParams];
        }
      });
      return buildSearchString(merged);
    },
    [params]
  );

  // Get current URL string
  const toUrlString = useCallback(() => {
    return buildSearchString(params);
  }, [params]);

  return {
    params,
    filters,
    search: filters.search,
    hasActiveFilters,
    setParam,
    setParams,
    clearParam,
    clearAllParams,
    toggleParam,
    buildUrl,
    toUrlString,
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create URL params for tag filter
 */
export function createTagFilterUrl(tagId: string, baseUrl: string = ''): string {
  return `${baseUrl}?tag=${tagId}`;
}

/**
 * Create URL params for project filter
 */
export function createProjectFilterUrl(projectId: string, baseUrl: string = ''): string {
  return `${baseUrl}?project=${projectId}`;
}

/**
 * Create URL params for customer filter
 */
export function createCustomerFilterUrl(customerId: string, baseUrl: string = ''): string {
  return `${baseUrl}?customer=${customerId}`;
}

/**
 * Create URL params for date filter
 */
export function createDateFilterUrl(date: string, baseUrl: string = ''): string {
  return `${baseUrl}?date=${date}`;
}

/**
 * Create URL params for date range filter
 */
export function createDateRangeFilterUrl(from: string, to: string, baseUrl: string = ''): string {
  return `${baseUrl}?from=${from}&to=${to}`;
}

/**
 * Create URL params for search
 */
export function createSearchUrl(query: string, baseUrl: string = ''): string {
  return `${baseUrl}?s=${encodeURIComponent(query)}`;
}

/**
 * Create URL params for status filter
 */
export function createStatusFilterUrl(status: string, baseUrl: string = ''): string {
  return `${baseUrl}?status=${status}`;
}

/**
 * Parse date string from URL (YYYY-MM-DD format)
 */
export function parseDateParam(dateStr: string | null): Date | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format date for URL (YYYY-MM-DD format)
 */
export function formatDateParam(date: Date): string {
  return date.toISOString().split('T')[0];
}
